import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCommandPalette } from '../context/CommandPaletteContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOAST_ID = 'palette-discovery-callout';

/**
 * One-time onboarding nudge that surfaces a Sonner toast on the user's
 * 3rd+ login until either:
 *  - they open the ⌘K palette (auto-dismiss + persist `palette_callout_shown`)
 *  - they click "Got it" on the toast (same)
 *
 * Mounts inside <AppShell> so it's available on every authenticated route.
 * Renders nothing — the toast is fired imperatively via Sonner.
 */
export default function PaletteCallout() {
  const { user, setUser } = useAuth();
  const { open: paletteOpen } = useCommandPalette();
  const firedRef = useRef(false);
  const dismissedRef = useRef(false);

  const persistShown = async () => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    try {
      const r = await axios.put(
        `${API_URL}/api/auth/preferences`,
        { palette_callout_shown: true },
      );
      if (r?.data?.preferences && setUser) {
        setUser((prev) => (prev ? { ...prev, preferences: r.data.preferences } : prev));
      }
    } catch { /* swallow — local dismissal is enough */ }
  };

  // Fire the toast once when criteria are met.
  useEffect(() => {
    if (firedRef.current) return;
    if (!user || user === false || user === null) return;

    const loginCount = user.login_count || 0;
    const alreadyShown = user.preferences?.palette_callout_shown === true;
    if (alreadyShown) return;
    if (loginCount < 3) return;

    // Detect macOS to render the right key glyph
    const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '');
    const shortcut = isMac ? '⌘ K' : 'Ctrl K';

    // Slight delay so the toast doesn't fight with route mount animations.
    // We deliberately set `firedRef.current` *inside* the timer callback
    // (rather than synchronously in the effect body) so React 18 StrictMode's
    // double-invocation of effects in dev doesn't cause the ref to short-circuit
    // the second pass before the toast actually renders.
    const t = setTimeout(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      toast(
        (
          <div className="flex items-start gap-3 py-1">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#4ABFCF]/20 to-[#0F2040]/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#0F2040]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#0F2040]">Pro tip — try the command palette</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                Press{' '}
                <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-white font-mono text-[10px] text-slate-700">
                  {shortcut}
                </kbd>{' '}
                anywhere to jump to estimates, run comparisons, or set component overrides without leaving the keyboard.
              </div>
            </div>
          </div>
        ),
        {
          id: TOAST_ID,
          duration: 12000,
          action: {
            label: 'Got it',
            onClick: () => { persistShown(); },
          },
          onDismiss: () => { persistShown(); },
          onAutoClose: () => { persistShown(); },
        }
      );
    }, 1200);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-dismiss + persist when the user opens the palette themselves.
  useEffect(() => {
    if (paletteOpen && firedRef.current && !dismissedRef.current) {
      toast.dismiss(TOAST_ID);
      persistShown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paletteOpen]);

  return null;
}
